import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Instagram, Youtube, Music2, Mail, ChevronDown } from "lucide-react";
import { ARTIST, TOUR_DATES } from "../mock";

const NAV = [
  { label: "Présentation", to: "/presentation" },
  {
    label: "Toutes les dates",
    to: "/tournee",
    children: TOUR_DATES.map((d) => ({
      label: `${d.day} ${d.month.toLowerCase()} — ${d.city}`,
      to: `/tournee#date-${d.id}`,
      highlight: d.highlight,
    })),
  },
  { label: "Merch", to: "/merch" },
  {
    label: "Événementiel",
    to: "/prestations",
    children: [
      { label: "Événements musicaux", to: "/prestations/evenements" },
      { label: "Photos", to: "/prestations/photos" },
      { label: "Vidéos", to: "/prestations/videos" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export default function Layout() {
  const { pathname, hash } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      // Wait for content to render before scrolling
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add("nav-target-flash");
          setTimeout(() => el.classList.remove("nav-target-flash"), 1600);
        }
      }, 120);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);

  return (
    <div className="grain relative">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-black/80 backdrop-blur-md border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" className="group flex items-center gap-3">
            <div className="w-9 h-9 border border-white/30 rounded-full flex items-center justify-center group-hover:border-white transition-colors duration-300">
              <span className="font-display text-lg leading-none">VR</span>
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-xl tracking-wide">VINCE RODI</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                Pop · Variété Française
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.to} className="relative group" data-testid={`nav-group-${n.to.replace(/\//g, "-")}`}>
                  <Link
                    to={n.to}
                    className={`flex items-center gap-1 text-sm uppercase tracking-[0.2em] transition-colors ${
                      pathname.startsWith(n.to)
                        ? "text-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    {n.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-black/95 backdrop-blur-md border border-white/10 min-w-[240px] py-2 max-h-[70vh] overflow-y-auto">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-sub-${c.to.replace(/[\/#]/g, "-")}`}
                          className={`flex items-center justify-between gap-3 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                            pathname + hash === c.to || pathname === c.to
                              ? "text-red-500"
                              : "text-neutral-300 hover:text-white hover:bg-white/5"
                          }`}
                        >
                          <span>{c.label}</span>
                          {c.highlight && (
                            <span className="text-[8px] tracking-[0.15em] bg-red-500 text-white px-1.5 py-0.5">
                              ★
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`text-sm uppercase tracking-[0.2em] transition-colors ${
                    pathname === n.to
                      ? "text-white"
                      : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {n.label}
                </Link>
              )
            )}
          </nav>

          <a
            href={ARTIST.linktree}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-2 border border-white/30 hover:border-white hover:bg-white hover:text-black transition-all duration-300 px-4 py-2 text-xs uppercase tracking-[0.2em]"
          >
            Écouter l'EP <ArrowUpRight size={14} />
          </a>
        </div>
      </header>

      <main className="relative z-10 pt-16 lg:pt-20">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-white/10 mt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="font-display text-5xl lg:text-7xl leading-[0.9] tracking-tight">
                VINCE<br />RODI
              </div>
              <p className="mt-6 text-neutral-400 max-w-md text-sm leading-relaxed">
                Pop française, sincère et lumineuse. Premier EP « Et Après » disponible, tournée nationale en juin 2026.
              </p>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Navigation</div>
              <ul className="space-y-2">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="text-sm text-neutral-300 hover:text-white transition-colors">
                      {n.label}
                    </Link>
                    {n.children && n.to !== "/tournee" && (
                      <ul className="mt-2 ml-3 space-y-1.5">
                        {n.children.map((c) => (
                          <li key={c.to}>
                            <Link to={c.to} className="text-xs text-neutral-500 hover:text-white transition-colors">
                              — {c.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Suivre · Contact</div>
              <div className="flex items-center gap-3 mb-5">
                <a href={ARTIST.linktree} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/20 hover:border-white hover:bg-white hover:text-black flex items-center justify-center transition-all">
                  <Music2 size={16} />
                </a>
                <a href="https://www.instagram.com/vincerodi84" target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/20 hover:border-white hover:bg-white hover:text-black flex items-center justify-center transition-all">
                  <Instagram size={16} />
                </a>
                <a href="https://www.youtube.com/@vincerodi84" target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/20 hover:border-white hover:bg-white hover:text-black flex items-center justify-center transition-all">
                  <Youtube size={16} />
                </a>
                <a href={`mailto:${ARTIST.email}`} className="w-10 h-10 border border-white/20 hover:border-white hover:bg-white hover:text-black flex items-center justify-center transition-all">
                  <Mail size={16} />
                </a>
              </div>
              <a href={`mailto:${ARTIST.email}`} className="block text-sm text-neutral-300 hover:text-white">
                {ARTIST.email}
              </a>
              <div className="mt-3 text-xs text-neutral-500">
                Booking · {ARTIST.labelEmail}
              </div>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-neutral-500">
            <div>© {new Date().getFullYear()} Vince Rodi. Tous droits réservés.</div>
            <div className="uppercase tracking-[0.25em]">Made with care</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
